import { Agent } from '@atproto/api'
import type { AtpAgent } from '@atproto/api'
import type { OAuthSession } from '@atproto/oauth-client-browser'
import { mapFeedViewPost } from '@/services/postsService'
import { fetchListFeed } from '@/services/listsService'
import type { PostsPage } from '@/types/posts'
import type { FeedGeneratorDetail, ResolvedFeed, SavedFeed } from '@/types/feeds'

export async function fetchSavedFeeds(session: OAuthSession): Promise<SavedFeed[]> {
    const agent = new Agent(session)
    const response = await agent.app.bsky.actor.getPreferences()

    const savedFeedsPref = response.data.preferences.find(
        pref => pref.$type === 'app.bsky.actor.defs#savedFeedsPrefV2'
    ) as { items?: SavedFeed[] } | undefined

    if (!savedFeedsPref?.items) return []

    return savedFeedsPref.items
}

export async function saveFeedPreferences(
    session: OAuthSession,
    items: SavedFeed[]
): Promise<void> {
    const agent = new Agent(session)
    const prefsResponse = await agent.app.bsky.actor.getPreferences()
    const prefs = prefsResponse.data.preferences

    const hasSavedFeedsPref = prefs.some(p => p.$type === 'app.bsky.actor.defs#savedFeedsPrefV2')

    const updatedPrefs = hasSavedFeedsPref
        ? prefs.map(pref =>
              pref.$type === 'app.bsky.actor.defs#savedFeedsPrefV2' ? { ...pref, items } : pref
          )
        : [...prefs, { $type: 'app.bsky.actor.defs#savedFeedsPrefV2', items }]

    await agent.app.bsky.actor.putPreferences({ preferences: updatedPrefs })
}

export async function fetchFeedGeneratorViews(
    session: OAuthSession,
    uris: string[]
): Promise<ResolvedFeed[]> {
    if (uris.length === 0) return []

    const agent = new Agent(session)
    const response = await agent.app.bsky.feed.getFeedGenerators({ feeds: uris })

    return response.data.feeds.map(gen => ({
        uri: gen.uri,
        displayName: gen.displayName,
        avatar: gen.avatar,
    }))
}

/** Timeout for the public `getFeedGenerators` call used to hydrate `feed` blocks. */
export const FEED_GENERATORS_TIMEOUT_MS = 10_000

/**
 * Batch-resolve feed generator views with the unauthenticated public agent.
 * Mirrors fetchEmbeddedPosts / fetchEmbeddedProfiles: one call, capped at 25.
 * Used to hydrate `feed` blocks in a Tahquamenon blocks document, where there
 * is no session-scoped agent to hand in.
 *
 * Bounded by `FEED_GENERATORS_TIMEOUT_MS` so a stalled AppView fails instead of
 * leaving the feed card loading forever; `signal` (React Query's own, when the
 * caller passes it) is combined in so query cancellation still aborts promptly.
 */
export async function fetchPublicFeedGeneratorViews(
    uris: string[],
    agent: AtpAgent,
    signal?: AbortSignal
): Promise<ResolvedFeed[]> {
    if (uris.length === 0) return []
    const timeoutSignal = AbortSignal.timeout(FEED_GENERATORS_TIMEOUT_MS)
    const response = await agent.app.bsky.feed.getFeedGenerators(
        { feeds: uris.slice(0, 25) },
        { signal: signal ? AbortSignal.any([signal, timeoutSignal]) : timeoutSignal }
    )
    return response.data.feeds.map(gen => ({
        uri: gen.uri,
        displayName: gen.displayName,
        avatar: gen.avatar,
    }))
}

export async function fetchFeedGeneratorDetail(
    session: OAuthSession,
    uri: string
): Promise<FeedGeneratorDetail> {
    const agent = new Agent(session)
    const response = await agent.app.bsky.feed.getFeedGenerator({ feed: uri })
    const gen = response.data.view
    return {
        uri: gen.uri,
        displayName: gen.displayName,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty description is the same as none for display purposes
        description: gen.description || undefined,
        avatar: gen.avatar,
        likeCount: gen.likeCount,
        creator: {
            did: gen.creator.did,
            handle: gen.creator.handle,
            displayName: gen.creator.displayName,
            avatarUrl: gen.creator.avatar,
        },
        acceptsInteractions: gen.acceptsInteractions,
        did: gen.did,
    }
}

/**
 * Returns true only for a well-formed feed generator AT URI:
 * `at://<authority>/app.bsky.feed.generator/<rkey>` — exactly three nonempty
 * path components with the collection exactly `app.bsky.feed.generator`.
 * Rejects list URIs, `'following'`, malformed URIs, query strings/fragments,
 * extra/missing segments, and encoded lookalikes.
 */
export function isFeedGeneratorFeedUri(uri: string | undefined): boolean {
    if (!uri) return false
    if (uri.includes('?') || uri.includes('#')) return false
    if (!uri.startsWith('at://')) return false
    const rest = uri.slice('at://'.length)
    const parts = rest.split('/')
    if (parts.length !== 3) return false
    const [authority, collection, rkey] = parts
    if (!authority || !rkey) return false
    return collection === 'app.bsky.feed.generator'
}

export interface ActorFeed {
    uri: string
    displayName: string
    description?: string
    avatar?: string
    likeCount?: number
    creator: {
        did: string
        handle: string
        displayName?: string
        avatarUrl?: string
    }
}

export interface ActorFeedsPage {
    feeds: ActorFeed[]
    cursor?: string
}

export async function fetchActorFeeds(
    session: OAuthSession,
    actor: string,
    options?: { cursor?: string; limit?: number }
): Promise<ActorFeedsPage> {
    const agent = new Agent(session)
    const response = await agent.app.bsky.feed.getActorFeeds({
        actor,
        limit: options?.limit ?? 20,
        cursor: options?.cursor,
    })

    return {
        feeds: response.data.feeds.map(feed => ({
            uri: feed.uri,
            displayName: feed.displayName,
            description: feed.description,
            avatar: feed.avatar,
            likeCount: feed.likeCount,
            creator: {
                did: feed.creator.did,
                handle: feed.creator.handle,
                displayName: feed.creator.displayName,
                avatarUrl: feed.creator.avatar,
            },
        })),
        cursor: response.data.cursor,
    }
}

export async function fetchCustomFeed(
    session: OAuthSession,
    feedUri: string,
    options?: { limit?: number; cursor?: string }
): Promise<PostsPage> {
    // Safety net: list URIs must use getListFeed, not getFeed.
    // If this fires, the caller has a routing bug — log a warning so regressions are visible.
    if (feedUri.includes('/app.bsky.graph.list/')) {
        console.warn(
            '[fetchCustomFeed] Received list URI — routing to fetchListFeed. ' +
                'This indicates a caller routing bug that should be fixed upstream.',
            feedUri
        )
        return fetchListFeed(session, feedUri, options)
    }
    const agent = new Agent(session)
    const response = await agent.app.bsky.feed.getFeed({
        feed: feedUri,
        limit: options?.limit ?? 20,
        cursor: options?.cursor,
    })
    const posts = response.data.feed.map(fvp => ({ ...mapFeedViewPost(fvp), fromFeedUri: feedUri }))
    return { posts, cursor: response.data.cursor }
}

/**
 * Send a single feed-tuning interaction (e.g. "show more/less like this") to a
 * feed generator via app.bsky.feed.sendInteractions. `feedUri` is required and
 * is sent as the top-level `feed` field. `feedContext`/`reqId` are included
 * only when defined — never on truthiness, so an empty string still passes
 * through, matching the values verbatim from the post that was fetched.
 *
 * Proxied to the feed generator's own service (`atproto-proxy: <generatorDid>#bsky_fg`),
 * matching the Bluesky reference client (social-app src/state/feed-feedback.tsx) — the
 * user's PDS doesn't implement sendInteractions and returns 501 when called directly, and
 * the AppView is not the correct target: interactions are recorded by the generator service.
 */
export async function sendFeedInteraction(
    session: OAuthSession,
    params: {
        feedUri: string
        postUri: string
        event: string
        feedContext?: string
        reqId?: string
        generatorDid: string
    }
): Promise<void> {
    const agent = new Agent(session).withProxy('bsky_fg', params.generatorDid)
    await agent.app.bsky.feed.sendInteractions({
        feed: params.feedUri,
        interactions: [
            {
                item: params.postUri,
                event: params.event,
                ...(params.feedContext !== undefined && { feedContext: params.feedContext }),
                ...(params.reqId !== undefined && { reqId: params.reqId }),
            },
        ],
    })
}
